import {
  createApi,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '@/app/store';
import { API_BASE_URL } from './apiBase';

/**
 * RTK Query's base API. The endpoints are injected by the client generated from
 * OpenAPI (`src/services/generated/api.ts`, via `npm run api:codegen`).
 *
 * - `baseUrl` is the **root of the same origin**, not `/api`: the spec's paths
 *   already include the prefix (`setGlobalPrefix('api')`), so putting it here
 *   would give `/api/api/dtes`. The spec also exposes routes outside the prefix
 *   (`/auth`, `/health`) that a `/api` baseUrl would make unreachable.
 * - `credentials: 'include'` sends the `HttpOnly` session cookie on every
 *   same-origin request. **That cookie is the SPA's credential**: the backend
 *   accepts it across all of `/api`, not only on `/api/account` (see
 *   `tenant-context.middleware.ts`). Only a tenant api key used to count, and
 *   the SPA had none to send, so all of `/api` answered 401.
 * - `X-Tenant-Id` says which of the person's tenants the call is addressed to.
 *   The backend validates it against their real memberships, so it **selects,
 *   does not grant**; it is sent whenever there is an active one because with
 *   more than one membership the backend demands a choice rather than guessing.
 * - `auth.apiKey` is still read in case the SPA ever operates with a key (today
 *   nobody fills it in): if present, it wins, just as in the backend.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const { apiKey, activeTenantId } = (getState() as RootState).auth;
      if (apiKey) {
        headers.set('Authorization', `Bearer ${apiKey}`);
      }
      if (activeTenantId) {
        headers.set('X-Tenant-Id', activeTenantId);
      }
      return headers;
    },
  }),
  // Empty on purpose: the generated client adds its own tag types with
  // `enhanceEndpoints({ addTagTypes })`, derived from the spec's tags.
  // Declaring them here too would duplicate them and they would drift apart when
  // the contract changes.
  tagTypes: [],
  endpoints: () => ({}),
});

/** Type guard for narrowing RTK Query errors in the UI. */
export function isFetchBaseQueryError(
  error: unknown,
): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error;
}
